import { drawCategoryTypeIds } from './eventConstants';
import { allStandingsEnabled, drawStandingsEnabledV2, renderRoundLabel, shouldDisplayDraws } from './events';
import { getDrawName } from './courtSchedular';
import { getValueByTypeId } from './types';

export const resolveDisplayedDraws = ({
    parentDraw,
    currentDraw,
    drawsDict,
    firstPublishedDraw,
}) => {
    const displayedParentDraw = (parentDraw?.pendingId && drawsDict?.[parentDraw.pendingId]) ||
        parentDraw?.data ||
        firstPublishedDraw ||
        null;

    const rawDisplayedCurrentDraw = (currentDraw?.pendingId && drawsDict?.[currentDraw.pendingId]) ||
        currentDraw?.data ||
        null;

    const isGroupedParent = displayedParentDraw?.categoryTypeId === drawCategoryTypeIds.GROUPED;
    const shouldFallbackToFirstChild = isGroupedParent &&
        rawDisplayedCurrentDraw?.id === displayedParentDraw.id;

    const displayedCurrentDraw = shouldFallbackToFirstChild
        ? (drawsDict?.[displayedParentDraw.id]?.childDraws?.[0] || rawDisplayedCurrentDraw)
        : rawDisplayedCurrentDraw;

    return { displayedParentDraw, displayedCurrentDraw, isGroupedParent };
};

export const buildChildDrawMenuItems = ({
    displayedParentDraw,
    drawsDict,
    drawCategoryTypesDict,
    isAllStandingsFlagEnabled,
    userDrawIds,
}) => {
    if (!displayedParentDraw || !drawsDict?.[displayedParentDraw.id]) {
        return [];
    }

    const isGroupedParent = displayedParentDraw.categoryTypeId === drawCategoryTypeIds.GROUPED;

    const childDraws = drawsDict[displayedParentDraw.id].childDraws
        .filter(shouldDisplayDraws)
        .map(cd => ({
            value: cd.id,
            label: getDrawName(cd, displayedParentDraw, drawCategoryTypesDict),
            playing: userDrawIds?.includes(cd.id),
        }));

    const isAllStandingsEnabled = displayedParentDraw.eliminationTypeId &&
        allStandingsEnabled(displayedParentDraw) &&
        isAllStandingsFlagEnabled;

    return [
        ...(isAllStandingsEnabled ? [{
            value: '',
            label: 'All standings',
        }] : []),
        ...(isGroupedParent ? [] : [{
            value: displayedParentDraw.id,
            label: getDrawName(displayedParentDraw, displayedParentDraw, drawCategoryTypesDict),
        }]),
        ...childDraws,
    ];
};

// Given the full draws list and the ids the user is placed in, pick the draw to
// auto-open: prefer a matching child draw (don't auto-load its first child), else
// a matching parent. Returns null when the user isn't in any draw.
export const resolveUserDrawSelection = (drawsData, userDrawIds) => {
    if (!Array.isArray(drawsData)) {
        return null;
    }

    let drawToSelect = null;
    let dontAutoLoadFirstChildDraw = false;

    drawsData.forEach((parentDraw) => {
        if (!parentDraw || !Array.isArray(parentDraw.childDraws)) {
            return;
        }
        parentDraw.childDraws.forEach((childDraw) => {
            if (userDrawIds.includes(childDraw.id) && !drawToSelect) {
                drawToSelect = childDraw.id;
                dontAutoLoadFirstChildDraw = true;
            }
        });
        if (userDrawIds.includes(parentDraw.id) && !drawToSelect) {
            drawToSelect = parentDraw.id;
            dontAutoLoadFirstChildDraw = false;
        }
    });

    return drawToSelect ? { drawToSelect, dontAutoLoadFirstChildDraw } : null;
};

const isRoundCutOff = (currentDraw, roundIndex) => currentDraw.data?.feedOutRound && roundIndex + 1 > currentDraw.data.feedOutRound;

const computeVisibleTabCount = (currentDraw, showStandings, activeRound) => {
    if (showStandings || !currentDraw.data?.rounds?.length) {
        return 1;
    }
    const validRounds = currentDraw.data.rounds.filter((_, i) => !isRoundCutOff(currentDraw, i));
    return Math.max(1, Math.min(3, validRounds.length - activeRound));
};

const buildRoundTab = ({ currentDraw, round, i, showStandings, activeRound, drawEliminationTypes, visibleCount }) => {
    const eliminationLabel = currentDraw.data.eliminationTypeId
        ? getValueByTypeId(drawEliminationTypes, currentDraw.data.eliminationTypeId)
        : 'default';
    const categoryLabel = currentDraw.data.categoryTypeId ? currentDraw.data.categoryTypeId : 'default';
    const isVisible = !showStandings && i >= activeRound && i <= activeRound + 2;

    return {
        title: renderRoundLabel(eliminationLabel, round, false, categoryLabel, currentDraw.data.rounds[i + 1]),
        titleName: eliminationLabel,
        showTab: true,
        active: !showStandings && activeRound === i,
        visible: isVisible,
        visibleIndex: isVisible ? i - activeRound : undefined,
        visibleCount: isVisible ? visibleCount : null,
    };
};

// Build the draw tab bar model: an optional leading Standings tab plus one tab per
// non-cut-off round, with visibility windowed around the active round.
export const buildDrawTabs = ({ currentDraw, showStandings, activeRound, drawEliminationTypes }) => {
    const standingsEnabled = drawStandingsEnabledV2(currentDraw.data?.eliminationTypeId);
    const visibleCount = computeVisibleTabCount(currentDraw, showStandings, activeRound);
    const tabs = [];

    if (standingsEnabled) {
        tabs.push({
            title: 'Standings',
            showTab: true,
            active: !!showStandings,
            visible: !!showStandings,
            visibleIndex: showStandings ? 0 : undefined,
            visibleCount: showStandings ? 1 : undefined,
        });
    }

    (currentDraw.data?.rounds ?? []).forEach((round, i) => {
        if (!isRoundCutOff(currentDraw, i)) {
            tabs.push(buildRoundTab({ currentDraw, round, i, showStandings, activeRound, drawEliminationTypes, visibleCount }));
        }
    });

    return { tabs, standingsEnabled };
};
